/**
 * RES-37 — Recetas sugeridas para el paso 3 del wizard de inventario.
 *
 * Se matchean por nombre normalizado del producto de la carta (sin tildes ni mayúsculas): una
 * sugerencia aplica si el nombre del producto contiene alguno de sus `match`. Gana la de match
 * más largo, así "pizza muzzarella" no cae en la genérica "pizza". Las líneas nombran insumos
 * de `inventoryTemplates.js`; si el dueño no los eligió en el paso 1 se suman al usarla.
 */

const line = (ingredient, quantity, unit) => ({ ingredient, quantity, unit });

export const RECIPE_TEMPLATES = [
  // Pizzería
  {
    id: 'pizza-muzzarella',
    match: ['pizza muzzarella', 'pizza mozzarella', 'muzzarella', 'mozzarella'],
    lines: [
      line('Masa de pizza', 250, 'g'),
      line('Muzzarella', 150, 'g'),
      line('Salsa de tomate', 80, 'ml'),
      line('Orégano', 1, 'g'),
    ],
  },
  {
    id: 'pizza-napolitana',
    match: ['napolitana'],
    lines: [
      line('Masa de pizza', 250, 'g'),
      line('Muzzarella', 150, 'g'),
      line('Salsa de tomate', 80, 'ml'),
      line('Tomate', 100, 'g'),
      line('Orégano', 1, 'g'),
    ],
  },
  {
    id: 'pizza-fugazzeta',
    match: ['fugazzeta', 'fugazza'],
    lines: [
      line('Masa de pizza', 250, 'g'),
      line('Muzzarella', 150, 'g'),
      line('Cebolla', 120, 'g'),
      line('Orégano', 1, 'g'),
    ],
  },
  {
    id: 'pizza-especial',
    match: ['especial', 'jamon y morron', 'jamón y morrón'],
    lines: [
      line('Masa de pizza', 250, 'g'),
      line('Muzzarella', 150, 'g'),
      line('Salsa de tomate', 80, 'ml'),
      line('Jamón cocido', 60, 'g'),
      line('Morrón', 40, 'g'),
    ],
  },
  {
    id: 'pizza',
    match: ['pizza'],
    lines: [
      line('Masa de pizza', 250, 'g'),
      line('Muzzarella', 150, 'g'),
      line('Salsa de tomate', 80, 'ml'),
    ],
  },
  // Hamburguesería
  {
    id: 'hamburguesa-cheddar-bacon',
    match: ['cheddar y bacon', 'bacon', 'panceta'],
    lines: [
      line('Pan de hamburguesa', 1, 'un'),
      line('Medallón de carne', 1, 'un'),
      line('Queso cheddar', 2, 'un'),
      line('Panceta', 30, 'g'),
    ],
  },
  {
    id: 'hamburguesa-clasica',
    match: ['hamburguesa clasica', 'hamburguesa clásica', 'hamburguesa', 'burger'],
    lines: [
      line('Pan de hamburguesa', 1, 'un'),
      line('Medallón de carne', 1, 'un'),
      line('Queso cheddar', 1, 'un'),
      line('Lechuga', 20, 'g'),
      line('Tomate', 30, 'g'),
    ],
  },
  {
    id: 'papas-fritas',
    match: ['papas fritas', 'papas'],
    lines: [line('Papas bastón congeladas', 250, 'g'), line('Sal fina', 2, 'g')],
  },
  // Cafetería
  {
    id: 'cafe-con-leche',
    match: ['cafe con leche', 'café con leche', 'latte'],
    lines: [
      line('Café en grano', 14, 'g'),
      line('Leche entera', 150, 'ml'),
      line('Vaso térmico', 1, 'un'),
    ],
  },
  {
    id: 'cortado',
    match: ['cortado', 'lagrima', 'lágrima'],
    lines: [line('Café en grano', 14, 'g'), line('Leche entera', 40, 'ml')],
  },
  {
    id: 'capuchino',
    match: ['capuchino', 'cappuccino'],
    lines: [
      line('Café en grano', 14, 'g'),
      line('Leche entera', 120, 'ml'),
      line('Cacao en polvo', 2, 'g'),
    ],
  },
  {
    id: 'cafe',
    match: ['espresso', 'cafe', 'café'],
    lines: [line('Café en grano', 14, 'g')],
  },
  {
    id: 'medialunas',
    match: ['medialuna'],
    lines: [line('Medialuna', 1, 'un')],
  },
  {
    id: 'tostado',
    match: ['tostado'],
    lines: [
      line('Pan de molde', 0.25, 'un'),
      line('Jamón cocido', 40, 'g'),
      line('Queso de máquina', 40, 'g'),
    ],
  },
  // Parrilla
  {
    id: 'choripan',
    match: ['choripan', 'choripán'],
    lines: [line('Chorizo', 1, 'un'), line('Pan francés', 1, 'un'), line('Chimichurri', 20, 'ml')],
  },
  {
    id: 'provoleta',
    match: ['provoleta'],
    lines: [line('Provoleta', 1, 'un')],
  },
  {
    id: 'bife-de-chorizo',
    match: ['bife de chorizo'],
    lines: [line('Bife de chorizo', 400, 'g')],
  },
  {
    id: 'vacio',
    match: ['vacio', 'vacío'],
    lines: [line('Vacío', 400, 'g')],
  },
  {
    id: 'asado',
    match: ['asado', 'tira de asado'],
    lines: [line('Asado', 400, 'g')],
  },
  // Bar
  {
    id: 'fernet-con-coca',
    match: ['fernet'],
    lines: [
      line('Fernet', 60, 'ml'),
      line('Gaseosa cola (línea)', 240, 'ml'),
      line('Hielo', 150, 'g'),
    ],
  },
  {
    id: 'gin-tonic',
    match: ['gin tonic', 'gin'],
    lines: [
      line('Gin', 50, 'ml'),
      line('Tónica', 200, 'ml'),
      line('Hielo', 150, 'g'),
      line('Limón', 10, 'g'),
    ],
  },
  {
    id: 'aperitivo-spritz',
    match: ['spritz', 'aperol'],
    lines: [
      line('Aperitivo rojo', 60, 'ml'),
      line('Espumante', 90, 'ml'),
      line('Soda', 30, 'ml'),
      line('Hielo', 150, 'g'),
    ],
  },
  {
    id: 'mojito',
    match: ['mojito'],
    lines: [
      line('Ron blanco', 50, 'ml'),
      line('Lima', 30, 'g'),
      line('Menta', 5, 'g'),
      line('Almíbar', 20, 'ml'),
      line('Soda', 60, 'ml'),
      line('Hielo', 150, 'g'),
    ],
  },
  // Sushi
  {
    id: 'roll-philadelphia',
    match: ['philadelphia', 'new york'],
    lines: [
      line('Arroz para sushi', 120, 'g'),
      line('Alga nori', 0.5, 'un'),
      line('Salmón rosado', 40, 'g'),
      line('Queso crema', 20, 'g'),
    ],
  },
  {
    id: 'roll',
    match: ['roll'],
    lines: [line('Arroz para sushi', 120, 'g'), line('Alga nori', 0.5, 'un')],
  },
  // Heladería
  {
    id: 'helado-kilo',
    match: ['1 kg', '1kg', 'kilo'],
    lines: [line('Base de helado de crema', 1, 'kg'), line('Pote 1 kg', 1, 'un')],
  },
  {
    id: 'helado-medio',
    match: ['1/2 kg', 'medio kilo'],
    lines: [line('Base de helado de crema', 500, 'g'), line('Pote 1/2 kg', 1, 'un')],
  },
  {
    id: 'helado-cuarto',
    match: ['1/4 kg', 'cuarto'],
    lines: [line('Base de helado de crema', 250, 'g'), line('Pote 1/4 kg', 1, 'un')],
  },
  {
    id: 'cucurucho',
    match: ['cucurucho', 'cono'],
    lines: [line('Base de helado de crema', 150, 'g'), line('Cucurucho', 1, 'un')],
  },
];
